import type { AskSynclySession, TemplateQuestionsResponse } from '@/entities/ask-syncly/ask-syncly-types';
import { socialApiClient } from '@/shared/api-client/social-api-client';
import { captureException } from '@sentry/react';
import { HTTPError } from 'ky';
import type { AskMode } from '../ask-syncly-constants';

/**
 * Create a new chat session for the given query ID
 * POST /queries/{query_id}/chat
 */
export async function createNewChatSession(
  workspaceId: string,
  queryId: string,
  mode: AskMode,
  signal?: AbortSignal,
): Promise<AskSynclySession> {
  return await socialApiClient
    .post(`workspaces/${workspaceId}/queries/${queryId}/chat`, {
      searchParams: {
        mode,
      },
      signal,
    })
    .json<AskSynclySession>();
}

/**
 * Fetch chat session for a given query ID
 * GET /queries/{query_id}/chat
 */
export async function fetchChatSession(
  workspaceId: string,
  queryId: string,
  mode: AskMode,
  signal?: AbortSignal,
): Promise<AskSynclySession> {
  try {
    let response = await socialApiClient
      .get(`workspaces/${workspaceId}/queries/${queryId}/sessions`, {
        searchParams: {
          mode,
        },
        signal,
      })
      .json<AskSynclySession | ''>();

    if (response === '') {
      // Create a new session
      await createNewChatSession(workspaceId, queryId, mode);
      response = await socialApiClient
        .get(`workspaces/${workspaceId}/queries/${queryId}/sessions`, {
          searchParams: { mode },
          signal,
        })
        .json<AskSynclySession>();
    }

    return response;
  } catch (error) {
    captureException(error);
    // Handle 204 No Content - create new session and fetch it
    if (error instanceof HTTPError) {
      // Fetch the newly created session
      const response = await socialApiClient
        .get(`workspaces/${workspaceId}/queries/${queryId}/sessions`, {
          searchParams: { mode },
          signal,
        })
        .json<AskSynclySession>();
      return response;
    }
    throw error;
  }
}

/**
 * Fetch template questions
 * GET /queries/{query_id}/templates
 */
export async function fetchTemplateQuestions(
  workspaceId: string,
  queryId: string,
  signal?: AbortSignal,
): Promise<TemplateQuestionsResponse> {
  const response = await socialApiClient
    .get(`workspaces/${workspaceId}/queries/${queryId}/templates`, { signal })
    .json<TemplateQuestionsResponse>();

  return response;
}

export async function fetchFollowUpQuestions(): Promise<TemplateQuestionsResponse> {
  const response = await socialApiClient.get('questions').json<TemplateQuestionsResponse>();

  return response;
}
